<div class="content">
  @if (!isShowingHelp) {
    <div class="wz-head">
      <span class="wz-eyebrow">World Creation Wizard</span>
      <h2 class="wz-title">Upload &amp; <span class="gold">validate map</span></h2>
    </div>

    <div class="wz-card up-intro">
      <p class="wz-muted">WorldTumbler needs two files from <strong>Azgaar's Fantasy Map Generator</strong>: the <strong>.map</strong> file and the full <strong>.json</strong> export.</p>
      <button type="button" class="btn btn-dark btn-sm" (click)="toggleHelp()">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.4c-.6.3-1 .8-1 1.5v.3"/><path d="M12 16.8h.01"/></svg>
        Where do I find them?
      </button>
    </div>

    <div class="up-form">
      <!-- Map name -->
      <div class="wz-field">
        <label class="wz-label" for="wb-map-name">Map name <span class="req">*</span></label>
        <input id="wb-map-name" type="text" class="wz-input" [(ngModel)]="mapName" [disabled]="mapNameConfirmed" placeholder="e.g. Seicland" maxlength="100" />
        <label class="up-lock" [class.is-locked]="mapNameConfirmed">
          <input type="checkbox" id="confirmMapName" [(ngModel)]="mapNameConfirmed" [disabled]="!mapName.trim()" />
          <span>Lock this name. It can't be changed later.</span>
        </label>
      </div>

      <!-- Genre -->
      <div class="wz-field">
        <label class="wz-label">Genre <span class="req">*</span></label>
        <ng-select class="wt-select" [(ngModel)]="selectedGenre" [items]="mapGenreList"
          bindValue="label" bindLabel="label" placeholder="Choose…" [clearable]="false" appendTo="body">
          <ng-template ng-label-tmp let-label="label"><span [class.wt-select-hint]="!label">{{ label || 'Choose…' }}</span></ng-template>
        </ng-select>
      </div>

      <!-- Map file -->
      <div class="wz-field">
        <span class="wz-label">Map file (.map) <span class="req">*</span></span>
        <div class="up-file" [class.has-file]="mapFileName" (click)="mapFileInput.click()" role="button" tabindex="0"
          (keydown.enter)="mapFileInput.click()">
          <span class="up-file-icon" aria-hidden="true">
            @if (mapFileName) {
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>
            } @else {
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>
            }
          </span>
          <span class="up-file-name">{{ mapFileName || 'No file chosen' }}</span>
          <span class="btn btn-dark btn-sm">{{ mapFileName ? 'Change' : 'Choose file' }}</span>
          <input type="file" #mapFileInput class="d-none" accept=".map" (change)="onMapFileSelected($event)" (click)="$event.stopPropagation()" />
        </div>
      </div>

      <!-- JSON file -->
      <div class="wz-field">
        <span class="wz-label">Full export (.json) <span class="req">*</span></span>
        <div class="up-file" [class.has-file]="jsonFileName" (click)="jsonFileInput.click()" role="button" tabindex="0"
          (keydown.enter)="jsonFileInput.click()">
          <span class="up-file-icon" aria-hidden="true">
            @if (jsonFileName) {
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>
            } @else {
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>
            }
          </span>
          <span class="up-file-name">{{ jsonFileName || 'No file chosen' }}</span>
          <span class="btn btn-dark btn-sm">{{ jsonFileName ? 'Change' : 'Choose file' }}</span>
          <input type="file" #jsonFileInput class="d-none" accept=".json" (change)="onJsonFileSelected($event)" (click)="$event.stopPropagation()" />
        </div>
      </div>
    </div>

    <div class="wz-actions">
      <button type="button" class="btn btn-dark" (click)="onBack()">Back</button>
      <button type="button" class="btn btn-theme" [disabled]="!mapNameConfirmed" (click)="onNext()">Next</button>
    </div>
  }

  @if (isShowingHelp) {
    <div class="wz-head">
      <span class="wz-eyebrow">World Creation Wizard</span>
      <h2 class="wz-title">Finding your <span class="gold">files</span></h2>
    </div>
    <p class="wz-lead">Both files come from Azgaar's Fantasy Map Generator. Both are required.</p>

    <ol class="up-help">
      <li class="wz-card">
        <span class="up-help-no">1</span>
        <p>Select <strong>Options → Export → To JSON → Full</strong>.</p>
        <img src="assets/images/first-json.png" alt="Azgaar export menu" />
        <span class="up-help-tag">.json file</span>
      </li>
      <li class="wz-card">
        <span class="up-help-no">2</span>
        <p>Open the generator and select <strong>Options → Save</strong>.</p>
        <img src="assets/images/middle-main.png" alt="Azgaar options menu" />
      </li>
      <li class="wz-card">
        <span class="up-help-no">3</span>
        <p>Under <strong>Save map</strong>, click <strong>machine</strong> to download the file.</p>
        <img src="assets/images/last-save.png" alt="Azgaar save menu" />
        <span class="up-help-tag">.map file</span>
      </li>
    </ol>

    <div class="wz-actions">
      <button type="button" class="btn btn-dark" (click)="toggleHelp()">Back to upload</button>
    </div>
  }
</div>
